import { StringKey } from '../../../lang/index';
import {
  EventTypeBackend,
  EventUnion,
  NewPoolEvent,
  POOL_EVENT_TYPES,
  PoolAdjustmentEvent,
} from '../../../types/events.types';
import { toNumber } from '../../../utils/to-number.util';

export type PoolEvent = NewPoolEvent | PoolAdjustmentEvent;

export type PoolEventRow = {
  date: Date;
  /** Signed change of a formal adjustment; null for the pool creation row. */
  delta: null | number;
  /** Size the pool was created with, or its size right after the adjustment. */
  sharesCount: number;
  transactionType: StringKey;
};

export const isPoolEvent = (event: EventUnion): event is PoolEvent =>
  POOL_EVENT_TYPES.includes(event.type);

export const isPoolAdjustmentEvent = (event: EventUnion): event is PoolAdjustmentEvent =>
  event.type === EventTypeBackend.POOL_INCREASE || event.type === EventTypeBackend.POOL_DECREASE;

/**
 * Ledger title of a formal adjustment, e.g. "Pool Increase - Employee Pool": unlike a pool creation
 * it has no name of its own, so it carries its pool's. `typeTitle` is the translated event type.
 */
export const getPoolAdjustmentTitle = (event: PoolAdjustmentEvent, typeTitle: string): string =>
  `${typeTitle} - ${event.pool.name}`;

export const resolvePoolEventRow = (event: PoolEvent): PoolEventRow => {
  if (event.type === EventTypeBackend.NEW_POOL) {
    return {
      date: event.pool.date,
      delta: null,
      // `dillutedSharesCount` is a bigint column (a string on the wire) and moves with every
      // later adjustment; the backend resolves the creation size for transaction rows.
      sharesCount: toNumber(event.initialDilutedSharesCount ?? event.pool.dillutedSharesCount),
      transactionType: 'POOL_CREATION',
    };
  }

  const isIncrease = event.type === EventTypeBackend.POOL_INCREASE;
  const sharesCount = toNumber(event.poolAdjustment.sharesCount);

  return {
    date: event.poolAdjustment.date,
    delta: isIncrease ? sharesCount : -sharesCount,
    sharesCount: toNumber(event.poolAdjustment.newDilutedSharesCount),
    transactionType: isIncrease ? 'POOL_INCREASE' : 'POOL_DECREASE',
  };
};
